import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { push } from 'connected-react-router';
import { Collapse } from '@mui';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  DATA_TYPES,
  ENTITY_DATA_TYPE,
  OVERLAP_DATA_TYPE
} from '@components/config/constants';
import MenuIcon from '@components/portal/menu/icon';
import OverflowTooltip from '@shared/overflow-tooltip';
import { getBasePath, getDataType } from '@utils/dashboard-utils';
import { verifyPermissions } from '@utils/permission-utils';
import './sidebar.scss';

const convertType = (type, subtype, parentDataType = null) => {
  let dataType = type === 'overlaps' ? OVERLAP_DATA_TYPE : type;
  let subType = subtype;
  const typeInfo = DATA_TYPES[dataType];

  if (!typeInfo) {
    subType = dataType;
    dataType = parentDataType === OVERLAP_DATA_TYPE ? OVERLAP_DATA_TYPE : ENTITY_DATA_TYPE;
  }

  return { dataType, subType, typeInfo: DATA_TYPES[dataType] };
};

const SidebarItem = ({
  item: { children, icon, label, permissions, subtype, type },
  parentDataType = null
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { dataType: currentDataType, subType: currentSubType } = params || {};
  const [open, setOpen] = useState(false);

  const { dataType, subType, typeInfo } = useMemo(
    () => convertType(type, subtype, parentDataType),
    [type, subtype, parentDataType]
  );

  const link = useMemo(() => {
    const portal = typeInfo?.portal || getBasePath();
    if (typeInfo?.subType && subType) {
      return `/${portal}/${dataType}/${subType}`;
    }
    return `/${portal}/${dataType}`;
  }, [dataType, subType, typeInfo]);

  const onClick = useCallback(
    () => {
      if (children) {
        setOpen(prev => !prev);
      } else {
        dispatch(push(link));
      }
    },
    [children, dispatch, link]
  );

  const onKeyDown = useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  }, [onClick]);

  const topLevelDataType = getDataType({ dataType: currentDataType }, location);
  useEffect(() => {
    if (children && topLevelDataType === dataType) {
      setOpen(true);
    }
  }, [children, dataType, topLevelDataType]);

  if (!verifyPermissions(permissions)) {
    return null;
  }

  const getActiveState = () => {
    if (children) {
      return topLevelDataType === dataType;
    }
    if (subType) {
      return currentSubType === subType && topLevelDataType === dataType;
    }
    return dataType === topLevelDataType;
  };
  const active = getActiveState();

  if (children) {
    return (
      <Fragment>
        <div
          onClick={onClick}
          onKeyDown={onKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          styleName={active ? 'menu-item has-children active' : 'menu-item has-children'}
        >
          <span styleName="dropdown-arrow">
            {open ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
          </span>
          {icon && (
            <span styleName="menu-icon">
              <MenuIcon icon={icon} type={subType} active={active} />
            </span>
          )}
          <OverflowTooltip>{label}</OverflowTooltip>
        </div>
        <Collapse in={open}>
          <div styleName="submenu">
            {children.map(child => (
              <SidebarItem
                key={child.type}
                item={child}
                parentDataType={dataType}
              />
            ))}
          </div>
        </Collapse>
      </Fragment>
    );
  }

  return (
    <div
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      styleName={active ? 'menu-item active' : 'menu-item'}
    >
      {icon && (
        <span styleName="menu-icon">
          <MenuIcon icon={icon} type={subType} active={active} />
        </span>
      )}
      <OverflowTooltip>{label}</OverflowTooltip>
    </div>
  );
};

SidebarItem.propTypes = {
  item: PropTypes.object,
  parentDataType: PropTypes.string
};

export default SidebarItem;
